import React, { useEffect, useState } from "react";
import { AlertCircle, AlertTriangle, Mail, Phone, ExternalLink, RotateCw, Truck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, extractErr } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const DISMISS_KEY = "operon_lowstock_dismissed_until";

const isOwnerRole = (role) => role === "owner" || role === "admin";

export function LowStockModal() {
  const [alerts, setAlerts] = useState(null);
  const [open, setOpen] = useState(false);
  const [resending, setResending] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = isOwnerRole(user?.role) || user?.is_master;

  useEffect(() => {
    const dismissUntil = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissUntil && Date.now() < dismissUntil) return;
    api.get("/dashboard/alerts").then(({ data }) => {
      setAlerts(data);
      const totalLow = (data?.low_stock?.length || 0) + (data?.critical?.length || 0);
      if (totalLow > 0) setOpen(true);
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 8 * 60 * 60 * 1000)); // 8h
    setOpen(false);
  };

  const view = () => {
    setOpen(false);
    navigate("/app/inventory?filter=low");
  };

  const resendEmail = async (item) => {
    setResending((p) => ({ ...p, [item.item_id]: true }));
    try {
      const { data } = await api.post(`/dashboard/alerts/email/${item.item_id}`);
      if (data.sent) toast.success(`Reorder email sent for ${item.sku}`);
      else toast.message(`Email send not delivered — check Resend configuration`);
    } catch (e) {
      toast.error(extractErr(e, "Failed to resend email"));
    }
    setResending((p) => ({ ...p, [item.item_id]: false }));
  };

  const total = (alerts?.low_stock?.length || 0) + (alerts?.critical?.length || 0);
  if (total === 0) return null;

  const all = [...(alerts?.critical || []), ...(alerts?.low_stock || [])];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl" data-testid="low-stock-alert-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-amber-500/10 text-amber-500 flex items-center justify-center">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle>Low stock — reorder needed</DialogTitle>
              <DialogDescription>
                {total} item{total === 1 ? "" : "s"} {total === 1 ? "is" : "are"} at or below minimum.
                {isOwner ? " You'll receive a reminder email automatically (max once per item per 24h)." : " Contact the workspace owner to arrange a reorder."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto space-y-2">
          {all.slice(0, 30).map((i) => {
            const isCritical = i.quantity <= 0;
            const suggested = i.reorder_qty > 0 ? i.reorder_qty : Math.max(i.min_stock * 2 - i.quantity, i.min_stock);
            return (
              <div
                key={i.item_id}
                className={`rounded-xl border p-3 ${isCritical ? "border-destructive/30 bg-destructive/[0.03]" : "border-amber-500/30 bg-amber-500/[0.03]"}`}
                data-testid={`low-stock-row-${i.item_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{i.name}</span>
                      <span className="font-mono text-[11px] text-muted-foreground">{i.sku}</span>
                      {isCritical && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive px-2 py-0.5 text-[10px] uppercase tracking-wide">
                          <AlertTriangle className="w-3 h-3" />Out of stock
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                      <span className={isCritical ? "text-destructive font-medium" : "text-amber-600 dark:text-amber-400 font-medium"}>
                        {i.quantity} {i.unit || ""} on hand
                      </span>
                      <span className="text-muted-foreground">min {i.min_stock} {i.unit || ""}</span>
                      <span className="rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 text-[11px]">
                        Suggest order: {suggested} {i.unit || ""}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Supplier block */}
                <div className="mt-2 pt-2 border-t border-border/60 text-xs">
                  {i.supplier_name ? (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="inline-flex items-center gap-1 font-medium">
                        <Truck className="w-3 h-3 text-primary" /> {i.supplier_name}
                      </span>
                      {i.supplier_email && (
                        <a href={`mailto:${i.supplier_email}?subject=Reorder request: ${encodeURIComponent(i.name)} (${i.sku})&body=${encodeURIComponent(`Hi,\n\nWe'd like to reorder:\n\nProduct: ${i.name}\nSKU: ${i.sku}\nQuantity: ${suggested} ${i.unit || ""}\n\nPlease confirm availability and lead time.\n\nThanks.`)}`}
                           className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`low-stock-mailto-${i.item_id}`}>
                          <Mail className="w-3 h-3" /> {i.supplier_email}
                        </a>
                      )}
                      {i.supplier_phone && (
                        <a href={`tel:${i.supplier_phone}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                          <Phone className="w-3 h-3" /> {i.supplier_phone}
                        </a>
                      )}
                      {isOwner && (
                        <Button size="sm" variant="ghost" className="ml-auto h-6 px-2 text-[11px]" onClick={() => resendEmail(i)} disabled={!!resending[i.item_id]} data-testid={`low-stock-resend-${i.item_id}`}>
                          <RotateCw className={`w-3 h-3 mr-1 ${resending[i.item_id] ? "animate-spin" : ""}`} />
                          Resend reorder email
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2 text-muted-foreground">
                      <span>⚠ No supplier configured for this item.</span>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => { setOpen(false); navigate("/app/inventory"); }}>
                        Add supplier <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {all.length > 30 && (
            <div className="text-center text-[11px] text-muted-foreground py-2">+ {all.length - 30} more items low</div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={dismiss} data-testid="low-stock-alert-dismiss-button">
            Dismiss for 8h
          </Button>
          <Button onClick={view} data-testid="low-stock-alert-view-button">View low-stock list</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
